// API client — points at VITE_API_BASE_URL or proxied /api in dev
const BASE = import.meta.env.VITE_API_BASE_URL ?? '';

/**
 * Check if the admin is logged in by reading the non-httpOnly indicator cookie.
 * The actual access token is in an httpOnly cookie that JS cannot read.
 */
export function isLoggedIn(): boolean {
  return document.cookie.split(';').some((c) => c.trim().startsWith('ospi_authenticated='));
}

export function clearLoginState(): void {
  // Clear the indicator cookie — the httpOnly token cookie is cleared by the server on logout.
  document.cookie = 'ospi_authenticated=; max-age=0; path=/';
}

// Legacy exports for backward compatibility during migration
export function getToken(): string | null {
  return isLoggedIn() ? '__cookie__' : null;
}
export function setToken(_token: string): void {
  // No-op — the server now sets the httpOnly cookie directly
}
export function clearToken(): void {
  clearLoginState();
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(init.headers as Record<string, string> ?? {}),
  };

  const res = await fetch(`${BASE}${path}`, { ...init, headers, credentials: 'include' });

  if (res.status === 401) {
    clearLoginState();
    window.location.href = '/login';
    throw new Error('Unauthorized');
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error((body as { error?: string }).error ?? `HTTP ${res.status}`);
  }

  return res.json() as Promise<T>;
}

// ── Auth ──────────────────────────────────────────────────────────────────────
export const api = {
  login: (email: string, password: string) =>
    request<{ accessToken: string; refreshToken: string; expiresIn: number }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),

  logoutBrowser: () =>
    request<{ message: string }>('/auth/logout-browser', { method: 'POST' }),

  // ── Users ─────────────────────────────────────────────────────────────────
  getUsers: (params?: { email?: string; status?: string; role?: string; limit?: number; offset?: number }) => {
    const q = new URLSearchParams();
    if (params?.email) q.set('email', params.email);
    if (params?.status) q.set('status', params.status);
    if (params?.role) q.set('role', params.role);
    if (params?.limit) q.set('limit', String(params.limit));
    if (params?.offset) q.set('offset', String(params.offset));
    return request<{ users: unknown[] }>(`/admin/users?${q}`);
  },

  getUser: (id: string) => request<{ user: unknown }>(`/admin/users/${id}`),

  grantTokens: (id: string, delta: number, note?: string) =>
    request<{ newBalance: number; delta: number }>(`/admin/users/${id}/tokens`, {
      method: 'PATCH',
      body: JSON.stringify({ delta, note }),
    }),

  setUserStatus: (id: string, status: 'active' | 'suspended') =>
    request<{ message: string }>(`/admin/users/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    }),

  deleteUser: (id: string) =>
    request<{ message: string }>(`/admin/users/${id}`, { method: 'DELETE' }),

  // ── Analytics ──────────────────────────────────────────────────────────────
  getAnalytics: () => request<{
    users: { total: number; active: number };
    aiRequests: { total: number };
    costs: { totalUsd: number };
    tokens: { totalSpent: number };
    modelBreakdown: { model: string; count: number; totalTokens: number; totalCost: string }[];
  }>('/admin/analytics'),

  getAiRequests: (query = '') =>
    request<{ requests: unknown[]; total: number; limit: number; offset: number }>(`/admin/ai-requests?${query}`),

  getAuditLog: (limit = 50, offset = 0) =>
    request<{ entries: unknown[] }>(`/admin/audit-log?limit=${limit}&offset=${offset}`),

  // ── Builds ─────────────────────────────────────────────────────────────────
  getBuilds: () => request<{ builds: unknown[] }>('/admin/builds'),

  activateBuild: (id: string) =>
    request<{ message: string }>(`/admin/builds/${id}/activate`, { method: 'PATCH' }),

  uploadBuild: (formData: FormData) => {
    return fetch(`${BASE}/builds`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    }).then((r) => r.json());
  },

  // ── Runtime Config ──────────────────────────────────────────────────────────
  getConfig: () =>
    request<{ config: Record<string, string> }>('/admin/config'),

  updateConfig: (updates: Record<string, string>) =>
    request<{ message: string; keys: string[]; config: Record<string, string> }>('/admin/config', {
      method: 'PATCH',
      body: JSON.stringify(updates),
    }),

  restartServer: () =>
    request<{ message: string }>('/admin/restart', { method: 'POST' }),

  // ── AI Stats ────────────────────────────────────────────────────────────────
  getAiStats: () =>
    request<{
      last24h: { total: number; jsonValid: number; retriesNeeded: number; successRate: string };
      last7d: { total: number; jsonValid: number; retriesNeeded: number; successRate: string };
      last30d: { total: number; jsonValid: number; retriesNeeded: number; successRate: string };
      byModel: { model: string; total: number; successRate: string }[];
    }>('/admin/ai-stats'),

  getSystemPrompts: () =>
    request<{ parse: string; chat: string; activeModel: string; flagshipModel: string }>('/admin/system-prompts'),

  // ── User Usage Stats ───────────────────────────────────────────────────────
  getUserUsageStats: () =>
    request<{
      stats: { userId: string; model: string; totalTokens: number; totalCost: string; requestCount: number }[];
    }>('/admin/users/usage-stats'),

  getTokenPricing: () =>
    request<{ activeModel: string; blendedCostPer1M: number | null; inputCostPer1M: number | null; outputCostPer1M: number | null }>('/admin/token-pricing'),

  // ── Model Registry ─────────────────────────────────────────────────────────
  getModels: () =>
    request<{ models: unknown[] }>('/admin/models'),

  refreshModels: () =>
    request<{ newModels: string[]; updatedCount: number }>('/admin/models/refresh', { method: 'POST' }),

  updateModel: (id: string, updates: Record<string, string>) =>
    request<{ message: string }>(`/admin/models/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    }),

  // ── Feedback ────────────────────────────────────────────────────────────────
  getFeedback: (params?: { status?: string; limit?: number; offset?: number }) => {
    const q = new URLSearchParams();
    if (params?.status) q.set('status', params.status);
    if (params?.limit) q.set('limit', String(params.limit));
    if (params?.offset) q.set('offset', String(params.offset));
    return request<{
      entries: {
        id: string;
        userId: string | null;
        category: string;
        screen: string | null;
        comment: string;
        flaggedFields: string[] | null;
        fieldValues: Record<string, string> | null;
        rawInput: string | null;
        metadata: Record<string, string> | null;
        aiConversation: unknown | null;
        status: string;
        createdAt: string;
        userEmail: string | null;
        attachmentCount: number;
      }[];
      total: number;
      limit: number;
      offset: number;
    }>(`/admin/feedback?${q}`);
  },

  updateFeedbackStatus: (id: string, status: string) =>
    request<{ message: string }>(`/admin/feedback/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    }),

  getFeedbackAttachments: (feedbackId: string) =>
    request<{
      attachments: {
        id: string;
        feedbackId: string;
        filename: string;
        mimeType: string;
        sizeBytes: number;
        storagePath: string;
        createdAt: string;
      }[];
    }>(`/admin/feedback/${feedbackId}/attachments`),

  getFeedbackAttachmentUrl: (feedbackId: string, attachmentId: string) =>
    `${BASE}/admin/feedback/${feedbackId}/attachments/${attachmentId}/file`,

  deleteFeedback: (id: string) =>
    request<{ message: string }>(`/admin/feedback/${id}`, { method: 'DELETE' }),
};
